
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Clock, Rocket, DollarSign, LineChart, Zap, Lightbulb, Target, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import StartupHero from "@/components/startup/StartupHero";
import StartupBenefits from "@/components/startup/StartupBenefits";
import StartupHowItWorks from "@/components/startup/StartupHowItWorks";
import StartupFeatures from "@/components/startup/StartupFeatures";
import StartupCTA from "@/components/startup/StartupCTA";

const StartupPage = () => {
  return (
    <div className="min-h-screen flex flex-col">
      <Helmet>
        <title>Google Ads for Startups | Budget-Smart Campaign Builder | Upfixe</title>
        <meta name="description" content="Launch Google Ads campaigns in 5 minutes with AI-powered optimization. Perfect for startups with limited budgets. Get more from less. Free to try." />
        <meta name="keywords" content="Google Ads for startups, budget Google Ads, startup customer acquisition, launch Google Ads" />
        <meta property="og:title" content="Google Ads for Startups | Budget-Smart Campaign Builder | Upfixe" />
        <meta property="og:description" content="Launch Google Ads campaigns in 5 minutes with AI-powered optimization. Perfect for startups with limited budgets." />
        <link rel="canonical" href="https://upfixe.com/startup" />
      </Helmet>
      <Header />
      
      <main className="flex-grow pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6">
          {/* Hero Section */}
          <StartupHero />

          {/* Benefits Section */}
          <StartupBenefits />

          {/* Video Demo Section */}
          <section className="py-24 px-4 bg-gradient-to-br from-background to-muted/50 mb-20">
            <div className="container mx-auto max-w-6xl">
              <div className="text-center mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Watch How Startups Scale Fast
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  See how startup founders launch campaigns that generate qualified leads on tight budgets. 
                  Perfect for bootstrapped teams who need results, not complexity.
                </p>
              </div>

              <div className="relative max-w-4xl mx-auto mb-12">
                <div className="aspect-video rounded-2xl overflow-hidden border-2 border-primary/20">
                  <iframe
                    src="https://www.youtube.com/embed/G9TxeAKXtbk"
                    title="Upfixe Startup Demo Video"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button 
                  size="lg" 
                  onClick={() => window.location.href = 'https://app.upfixe.com'}
                  className="text-lg px-8"
                >
                  Launch Campaign Now for Free
                </Button>
                <Button 
                  size="lg" 
                  variant="outline"
                  className="text-lg px-8"
                  onClick={() => window.open('https://youtu.be/G9TxeAKXtbk', '_blank')}
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Full Demo
                </Button>
              </div>
            </div>
          </section>

          {/* How It Works Section */}
          <StartupHowItWorks />

          {/* Features Section */}
          <StartupFeatures />

          {/* Blog Links Section */}
          <section className="py-20 px-4 bg-blue-50 rounded-2xl mb-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Learn How Startups Grow on a Budget</h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                Discover strategies for bootstrap customer acquisition, efficient budget allocation, and scaling without massive marketing budgets.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <a href="/blog/reduce-google-ads-cpa" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Reduce Google Ads CPA</h3>
                <p className="text-sm text-muted-foreground mb-4">7 proven strategies to cut your cost-per-acquisition in half.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/budget-optimization-guide" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">Budget Optimization Guide</h3>
                <p className="text-sm text-muted-foreground mb-4">Complete framework for maximizing ROI on tight budgets.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
              <a href="/blog/ai-vs-manual-google-ads" className="group bg-white p-6 rounded-lg hover:shadow-lg transition-shadow">
                <h3 className="font-semibold text-lg mb-2 group-hover:text-blue-600">AI vs Manual Ads</h3>
                <p className="text-sm text-muted-foreground mb-4">Compare costs and results: agencies vs AI platforms vs DIY.</p>
                <span className="text-sm font-semibold text-blue-600">Read Article →</span>
              </a>
            </div>
          </section>

          {/* Call to Action */}
          <StartupCTA />
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default StartupPage;
